import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Search, X } from "lucide-react";
import toast from "react-hot-toast";
import InvestmentMonthWindowsGrid from "./InvestmentMonthWindowsGrid";
import DeclarationWorkflowBlock from "./payroll-settings/DeclarationWorkflowBlock";
import TaxYearLockModal from "./payroll-settings/TaxYearLockModal";
import { getCurrentFinancialYear } from "./tax-planning/taxPlanning";
import {
  buildFinancialYearOptions,
  getServiceCommenceYear,
  normalizeFinancialYearKey,
  normalizeLockedTaxYears,
  readCompanyDataFromStorage,
} from "./payroll-settings/taxYearUtils";
import {
  DEFAULT_WORKFLOW,
  normalizeWorkflow,
  validateWorkflow,
} from "./payroll-settings/declarationWorkflowDefaults";
import {
  FY_INVESTMENT_MONTHS,
  buildInvestmentWindowsPayload,
  buildProofWindowsPayload,
  createDefaultInvestmentWindows,
  createDefaultProofWindows,
  normalizeWindowsFromSettings,
  validateInvestmentWindows,
  validateProofWindows,
} from "../../utils/investmentDeclarationWindows";

/**
 * Declaration & Proof Submission settings (Payroll Settings).
 * Exposes loadFromSettings / validate / buildPayload via ref.
 */
const InvestmentDeclarationSettings = forwardRef(function InvestmentDeclarationSettings(
  {
    darkMode = false,
    employees = [],
    settings = null,
    onFlagsChange,
    canEdit = true,
    saving = false,
    onSave,
  },
  ref
) {
  const [taxYear, setTaxYear] = useState(getCurrentFinancialYear());
  const [lockedTaxYears, setLockedTaxYears] = useState([]);
  const [defaultTaxRegime, setDefaultTaxRegime] = useState("NEW");
  const [showLockModal, setShowLockModal] = useState(false);
  const [enableInvestmentDeclaration, setEnableInvestmentDeclaration] =
    useState(false);
  const [investmentWindows, setInvestmentWindows] = useState(
    createDefaultInvestmentWindows
  );
  const [enableInvestmentDeclarationWithProof, setEnableInvestmentDeclarationWithProof] =
    useState(false);
  const [proofWindows, setProofWindows] = useState(createDefaultProofWindows);
  const [enableTaxRegimeLock, setEnableTaxRegimeLock] = useState(true);
  const [declarationWorkflow, setDeclarationWorkflow] = useState(DEFAULT_WORKFLOW);
  const [proofWorkflow, setProofWorkflow] = useState(DEFAULT_WORKFLOW);
  // Shared selection / view modals (kind: investment | proof)
  const [selectionKind, setSelectionKind] = useState("investment");
  const [showEmployeeSelectionModal, setShowEmployeeSelectionModal] =
    useState(false);
  const [selectionTargetMonth, setSelectionTargetMonth] = useState(null);
  const [selectionDraftIds, setSelectionDraftIds] = useState([]);
  const [selectionSearch, setSelectionSearch] = useState("");
  const [viewSelectedMonth, setViewSelectedMonth] = useState(null);
  const [viewKind, setViewKind] = useState("investment");

  const companyData = useMemo(() => readCompanyDataFromStorage(), []);
  const serviceYear = getServiceCommenceYear(companyData);
  const fyOptions = useMemo(
    () => buildFinancialYearOptions(serviceYear),
    [serviceYear]
  );

  const loadFromSettings = (apiSettings) => {
    if (!apiSettings) return;
    const savedFy = apiSettings.taxYear || getCurrentFinancialYear();
    const fyMatch =
      fyOptions.find(
        (fy) => normalizeFinancialYearKey(fy) === normalizeFinancialYearKey(savedFy)
      ) ||
      fyOptions[fyOptions.length - 1] ||
      savedFy;
    setTaxYear(fyMatch);
    const locked = normalizeLockedTaxYears(apiSettings.lockedTaxYears).map(
      (y) =>
        fyOptions.find(
          (fy) => normalizeFinancialYearKey(fy) === normalizeFinancialYearKey(y)
        ) || normalizeFinancialYearKey(y)
    );
    if (
      apiSettings.isTaxYearLocked &&
      apiSettings.taxYear &&
      !locked.some(
        (y) =>
          normalizeFinancialYearKey(y) ===
          normalizeFinancialYearKey(apiSettings.taxYear)
      )
    ) {
      locked.push(fyMatch);
    }
    setLockedTaxYears(locked);
    const regime = String(apiSettings.defaultTaxRegime || "NEW").toUpperCase();
    setDefaultTaxRegime(regime === "OLD" ? "OLD" : "NEW");
    setEnableInvestmentDeclaration(!!apiSettings.isInvestmentEnabled);
    setInvestmentWindows(
      normalizeWindowsFromSettings(apiSettings.investmentWindows, "investment")
    );
    setEnableInvestmentDeclarationWithProof(!!apiSettings.isProofEnabled);
    setProofWindows(
      normalizeWindowsFromSettings(apiSettings.proofWindows, "proof")
    );
    setEnableTaxRegimeLock(apiSettings.isTaxRegimeLocked !== false);
    setDeclarationWorkflow(normalizeWorkflow(apiSettings.declarationWorkflow));
    setProofWorkflow(normalizeWorkflow(apiSettings.proofWorkflow));
  };

  useEffect(() => {
    if (settings) loadFromSettings(settings);
  }, [settings, fyOptions]);

  useEffect(() => {
    onFlagsChange?.({
      enableInvestmentDeclaration,
      enableInvestmentDeclarationWithProof,
      enableTaxRegimeLock,
    });
  }, [
    enableInvestmentDeclaration,
    enableInvestmentDeclarationWithProof,
    enableTaxRegimeLock,
    onFlagsChange,
  ]);

  useEffect(() => {
    if (!enableInvestmentDeclaration && !enableInvestmentDeclarationWithProof) {
      setEnableTaxRegimeLock(true);
    }
  }, [enableInvestmentDeclaration, enableInvestmentDeclarationWithProof]);

  const getWindowsSetter = (kind) =>
    kind === "proof" ? setProofWindows : setInvestmentWindows;

  const getWindowsState = (kind) =>
    kind === "proof" ? proofWindows : investmentWindows;

  const updateMonth = (kind, monthNum, patch) => {
    getWindowsSetter(kind)((prev) => {
      const primaryFlag =
        kind === "proof" ? "allowExited" : "allowNewJoinees";
      const current = prev.months?.[monthNum] || {
        enabled: false,
        fromDay: 1,
        toDay: 31,
        [primaryFlag]: false,
        allowAll: true,
        allowSelection: false,
        selectedEmployeeIds: [],
      };
      const next = { ...current, ...patch };
      if (patch.fromDay != null) {
        const fromDay = Number(patch.fromDay);
        if (Number(next.toDay) < fromDay) next.toDay = fromDay;
      }
      return {
        ...prev,
        months: { ...prev.months, [monthNum]: next },
      };
    });
  };

  const openEmployeeSelectionModal = (kind, monthNum) => {
    const cfg = getWindowsState(kind).months?.[monthNum] || {};
    setSelectionKind(kind);
    setSelectionTargetMonth(monthNum);
    setSelectionDraftIds([...(cfg.selectedEmployeeIds || [])]);
    setSelectionSearch("");
    setShowEmployeeSelectionModal(true);
  };

  const closeEmployeeSelectionModal = (revertIfIncomplete = false) => {
    if (revertIfIncomplete && selectionTargetMonth != null) {
      const cfg = getWindowsState(selectionKind).months?.[selectionTargetMonth];
      if (cfg?.allowSelection && !(cfg.selectedEmployeeIds || []).length) {
        updateMonth(selectionKind, selectionTargetMonth, {
          allowSelection: false,
        });
      }
    }
    setShowEmployeeSelectionModal(false);
    setSelectionTargetMonth(null);
  };

  const applyEmployeeSelectionModal = () => {
    if (selectionTargetMonth == null) {
      toast.error("No month selected for employee selection");
      return;
    }
    if (!selectionDraftIds.length) {
      toast.error("Please select at least one employee");
      return;
    }
    updateMonth(selectionKind, selectionTargetMonth, {
      enabled: true,
      allowSelection: true,
      selectedEmployeeIds: [...selectionDraftIds],
    });
    setShowEmployeeSelectionModal(false);
    setSelectionTargetMonth(null);
  };

  const selectionEmployeeOptions = useMemo(() => {
    const q = selectionSearch.trim().toLowerCase();
    return (employees || []).filter((emp) => {
      if (!q) return true;
      const code = String(
        emp?.empCode || emp?.emp_code || emp?.employeeCode || ""
      ).toLowerCase();
      const name = String(
        emp?.user?.name ||
          emp?.fullName ||
          emp?.name ||
          [emp?.firstName, emp?.lastName].filter(Boolean).join(" ") ||
          ""
      ).toLowerCase();
      return code.includes(q) || name.includes(q);
    });
  }, [employees, selectionSearch]);

  const getEmployeeLabel = (emp) => {
    if (!emp) return null;
    const code = emp.empCode || emp.emp_code || emp.employeeCode || "";
    const name =
      emp.user?.name ||
      emp.fullName ||
      emp.name ||
      [emp.firstName, emp.lastName].filter(Boolean).join(" ") ||
      "Unnamed";
    return { id: Number(emp.id), code, name };
  };

  const viewedSelectedEmployees = useMemo(() => {
    if (viewSelectedMonth == null) return [];
    const windows = getWindowsState(viewKind);
    const ids =
      windows.months?.[viewSelectedMonth]?.selectedEmployeeIds || [];
    const byId = new Map(
      (employees || []).map((e) => [Number(e.id), getEmployeeLabel(e)])
    );
    return ids.map((id) => {
      const found = byId.get(Number(id));
      return (
        found || { id: Number(id), code: `#${id}`, name: "Unknown employee" }
      );
    });
  }, [viewSelectedMonth, viewKind, investmentWindows, proofWindows, employees]);

  const viewedMonthLabel =
    FY_INVESTMENT_MONTHS.find((m) => m.num === viewSelectedMonth)?.label || "";

  useImperativeHandle(ref, () => ({
    loadFromSettings,
    isTaxRegimeLockForced: () =>
      !enableInvestmentDeclaration && !enableInvestmentDeclarationWithProof,
    validate: () => {
      if (enableInvestmentDeclaration) {
        const inv = validateInvestmentWindows(investmentWindows);
        if (!inv.ok) return inv;
        const wf = validateWorkflow(declarationWorkflow, "Declaration workflow");
        if (!wf.ok) return wf;
      }
      if (enableInvestmentDeclarationWithProof) {
        const proof = validateProofWindows(proofWindows);
        if (!proof.ok) {
          return {
            ...proof,
            message: `Proof Submission — ${proof.message || "Invalid windows"}`,
          };
        }
        const wf = validateWorkflow(proofWorkflow, "Proof Submission workflow");
        if (!wf.ok) return wf;
      }
      return { ok: true };
    },
    buildPayload: () => {
      const taxRegimeLockDisabled =
        !enableInvestmentDeclaration && !enableInvestmentDeclarationWithProof;
      const effectiveTaxRegimeLock = taxRegimeLockDisabled
        ? true
        : enableTaxRegimeLock;
      const effectiveTaxYear = String(taxYear || getCurrentFinancialYear()).trim();
      const effectiveLocks = normalizeLockedTaxYears(lockedTaxYears);

      return {
        taxYear: effectiveTaxYear,
        lockedTaxYears: effectiveLocks,
        isTaxYearLocked: effectiveLocks
          .map(normalizeFinancialYearKey)
          .includes(normalizeFinancialYearKey(effectiveTaxYear)),
        defaultTaxRegime: defaultTaxRegime === "OLD" ? "OLD" : "NEW",
        isInvestmentEnabled: enableInvestmentDeclaration,
        investmentMode: "ALWAYS",
        investmentFromDay: null,
        investmentToDay: null,
        investmentEndDate: null,
        investmentWindows: buildInvestmentWindowsPayload(investmentWindows),
        isProofEnabled: enableInvestmentDeclarationWithProof,
        proofMode: "ALWAYS",
        proofMonth: null,
        proofFromDay: null,
        proofToDay: null,
        proofEndDate: null,
        proofWindows: buildProofWindowsPayload(proofWindows),
        isTaxRegimeLocked: effectiveTaxRegimeLock,
        declarationWorkflow: normalizeWorkflow(declarationWorkflow),
        proofWorkflow: normalizeWorkflow(proofWorkflow),
      };
    },
  }));

  const selectCls = `px-2 py-1.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 disabled:opacity-50 ${
    darkMode
      ? "bg-gray-600 border-gray-500 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const taxRegimeLockDisabled =
    !enableInvestmentDeclaration && !enableInvestmentDeclarationWithProof;
  const effectiveTaxRegimeLock = taxRegimeLockDisabled
    ? true
    : enableTaxRegimeLock;

  const resetDeclarationSection = () => {
    if (settings) {
      setEnableInvestmentDeclaration(!!settings.isInvestmentEnabled);
      setInvestmentWindows(
        normalizeWindowsFromSettings(settings.investmentWindows, "investment")
      );
      setDeclarationWorkflow(normalizeWorkflow(settings.declarationWorkflow));
    } else {
      setEnableInvestmentDeclaration(false);
      setInvestmentWindows(createDefaultInvestmentWindows());
      setDeclarationWorkflow(normalizeWorkflow(DEFAULT_WORKFLOW));
    }
    toast.success("Declaration settings reset");
  };

  const resetProofSection = () => {
    if (settings) {
      setEnableInvestmentDeclarationWithProof(!!settings.isProofEnabled);
      setProofWindows(normalizeWindowsFromSettings(settings.proofWindows, "proof"));
      setProofWorkflow(normalizeWorkflow(settings.proofWorkflow));
    } else {
      setEnableInvestmentDeclarationWithProof(false);
      setProofWindows(createDefaultProofWindows());
      setProofWorkflow(normalizeWorkflow(DEFAULT_WORKFLOW));
    }
    toast.success("Proof Submission settings reset");
  };

  const saveBtnCls =
    "rounded-full px-4 py-2 text-sm font-medium rh-btn-primary disabled:opacity-50";
  const resetBtnCls = `rounded-full border px-4 py-2 text-sm font-medium disabled:opacity-50 ${
    darkMode
      ? "border-gray-500 bg-gray-600 text-white hover:bg-gray-550"
      : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
  }`;

  const sectionActions = ({ showReset = false, onReset } = {}) => (
    <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
      {showReset && (
        <button
          type="button"
          disabled={!canEdit || saving}
          onClick={onReset}
          className={resetBtnCls}
        >
          Reset
        </button>
      )}
      <button
        type="button"
        disabled={!canEdit || saving}
        onClick={() => onSave?.()}
        className={saveBtnCls}
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );

  const sectionShell = `rounded-2xl border p-4 ${
    darkMode ? "border-gray-600" : "border-gray-200"
  }`;

  const toggleCls = `w-11 h-6 rounded-full peer ${
    darkMode ? "bg-gray-600" : "bg-gray-200"
  } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-redhalo-red`;

  return (
    <>
      <div className={`mb-5 space-y-5 ${sectionShell}`}>
        <div>
          <h4 className="text-base font-medium mb-3">Tax Year</h4>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Active Tax Year
              </label>
              <select
                value={taxYear}
                onChange={(e) => {
                  const next = e.target.value;
                  setTaxYear(next);
                  setLockedTaxYears((prev) => prev.filter((y) => y !== next));
                }}
                disabled={!canEdit || fyOptions.length === 0}
                className={`min-w-[10rem] rounded-xl border px-3 py-2 text-sm ${
                  darkMode
                    ? "bg-gray-600 border-gray-500 text-white"
                    : "bg-white border-gray-300"
                }`}
              >
                {fyOptions.map((fy) => (
                  <option key={fy} value={fy}>
                    {fy}
                    {lockedTaxYears.includes(fy) ? " (Locked)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => setShowLockModal(true)}
              className="rounded-full border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 dark:border-gray-500 dark:bg-gray-600 dark:hover:bg-gray-550"
            >
              Lock / Unlock years…
            </button>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 dark:border-gray-600">
          <h5 className="text-sm font-medium mb-3">Default Tax Regime</h5>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="defaultTaxRegime"
                value="NEW"
                checked={defaultTaxRegime === "NEW"}
                disabled={!canEdit}
                onChange={() => setDefaultTaxRegime("NEW")}
              />
              <span>New Regime</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="defaultTaxRegime"
                value="OLD"
                checked={defaultTaxRegime === "OLD"}
                disabled={!canEdit}
                onChange={() => setDefaultTaxRegime("OLD")}
              />
              <span>Old Regime</span>
            </label>
          </div>
        </div>
        {sectionActions()}
      </div>

      {/* Declaration */}
      <div className={`mb-5 ${sectionShell}`}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <h4 className="text-base font-medium">Declaration</h4>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={enableInvestmentDeclaration}
              onChange={(e) =>
                setEnableInvestmentDeclaration(e.target.checked)
              }
              className="sr-only peer"
            />
            <div className={toggleCls} />
          </label>
        </div>

        {enableInvestmentDeclaration && (
          <div>
            <InvestmentMonthWindowsGrid
              kind="investment"
              darkMode={darkMode}
              windows={investmentWindows}
              onUpdateMonth={(monthNum, patch) =>
                updateMonth("investment", monthNum, patch)
              }
              onOpenSelection={(monthNum) =>
                openEmployeeSelectionModal("investment", monthNum)
              }
              onViewSelected={(monthNum) => {
                setViewKind("investment");
                setViewSelectedMonth(monthNum);
              }}
              selectionTargetMonth={
                showEmployeeSelectionModal && selectionKind === "investment"
                  ? selectionTargetMonth
                  : null
              }
              selectCls={selectCls}
            />
            <DeclarationWorkflowBlock
              darkMode={darkMode}
              title="Declaration approval workflow"
              value={declarationWorkflow}
              onChange={setDeclarationWorkflow}
            />
          </div>
        )}
        {sectionActions({
          showReset: true,
          onReset: resetDeclarationSection,
        })}
      </div>

      {/* Proof Submission */}
      <div className={`mb-5 ${sectionShell}`}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <h4 className="text-base font-medium">Proof Submission</h4>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={enableInvestmentDeclarationWithProof}
              onChange={(e) =>
                setEnableInvestmentDeclarationWithProof(e.target.checked)
              }
              className="sr-only peer"
            />
            <div className={toggleCls} />
          </label>
        </div>

        {enableInvestmentDeclarationWithProof && (
          <div>
            <InvestmentMonthWindowsGrid
              kind="proof"
              darkMode={darkMode}
              windows={proofWindows}
              onUpdateMonth={(monthNum, patch) =>
                updateMonth("proof", monthNum, patch)
              }
              onOpenSelection={(monthNum) =>
                openEmployeeSelectionModal("proof", monthNum)
              }
              onViewSelected={(monthNum) => {
                setViewKind("proof");
                setViewSelectedMonth(monthNum);
              }}
              selectionTargetMonth={
                showEmployeeSelectionModal && selectionKind === "proof"
                  ? selectionTargetMonth
                  : null
              }
              selectCls={selectCls}
            />
            <DeclarationWorkflowBlock
              darkMode={darkMode}
              title="Proof Submission approval workflow"
              value={proofWorkflow}
              onChange={setProofWorkflow}
            />
          </div>
        )}
        {sectionActions({
          showReset: true,
          onReset: resetProofSection,
        })}
      </div>

      {/* Tax Regime Lock */}
      <div className={sectionShell}>
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-base font-medium mb-1">
              Tax Regime Selection Lock
            </h4>
            {taxRegimeLockDisabled && (
              <p
                className={`text-xs mt-1 ${
                  darkMode ? "text-amber-400" : "text-amber-600"
                }`}
              >
                Turn on Declaration or Proof to unlock this setting
              </p>
            )}
          </div>
          <label
            className={`relative inline-flex items-center ${
              taxRegimeLockDisabled
                ? "cursor-not-allowed opacity-90"
                : "cursor-pointer"
            }`}
          >
            <input
              type="checkbox"
              checked={effectiveTaxRegimeLock}
              disabled={taxRegimeLockDisabled}
              onChange={(e) => setEnableTaxRegimeLock(e.target.checked)}
              className="sr-only peer"
            />
            <div
              className={`w-11 h-6 rounded-full peer ${
                darkMode ? "bg-gray-600" : "bg-gray-200"
              } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[hsl(var(--ds-primary))] peer-disabled:opacity-90`}
            />
          </label>
        </div>
        {sectionActions()}
      </div>

      {/* View selected employees */}
      {viewSelectedMonth != null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div
            className={`rounded-2xl shadow-xl w-[480px] max-h-[75vh] overflow-hidden flex flex-col ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold">
                Selected employees — {viewedMonthLabel}
                {viewKind === "proof" ? " (Proof)" : ""}
              </h3>
              <button
                type="button"
                onClick={() => setViewSelectedMonth(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {viewedSelectedEmployees.length === 0 ? (
                <p
                  className={`text-sm ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No employees selected for this month.
                </p>
              ) : (
                <ul
                  className={`border rounded-xl divide-y ${
                    darkMode
                      ? "border-gray-600 divide-gray-700"
                      : "border-gray-200 divide-gray-100"
                  }`}
                >
                  {viewedSelectedEmployees.map((emp) => (
                    <li
                      key={emp.id}
                      className="px-3 py-2 text-sm flex items-center gap-2"
                    >
                      <span className="font-medium">{emp.code}</span>
                      <span
                        className={
                          darkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        {emp.name}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={() => {
                  const month = viewSelectedMonth;
                  const kind = viewKind;
                  setViewSelectedMonth(null);
                  if (month != null) openEmployeeSelectionModal(kind, month);
                }}
                className="px-4 py-2 text-sm rounded-full rh-btn-primary"
              >
                Edit selection
              </button>
              <button
                type="button"
                onClick={() => setViewSelectedMonth(null)}
                className={`px-4 py-2 text-sm rounded-full ${
                  darkMode
                    ? "bg-gray-600 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employee selection modal */}
      {showEmployeeSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div
            className={`rounded-2xl shadow-xl w-[560px] max-h-[85vh] overflow-hidden flex flex-col ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold">
                Select employees
                {selectionTargetMonth
                  ? ` — ${
                      FY_INVESTMENT_MONTHS.find(
                        (m) => m.num === selectionTargetMonth
                      )?.label || ""
                    }`
                  : ""}
                {selectionKind === "proof" ? " (Proof)" : ""}
              </h3>
              <button
                type="button"
                onClick={() => closeEmployeeSelectionModal(true)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              <p
                className={`text-xs ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Window uses this month&apos;s From day → To day. Select employees
                who can {selectionKind === "proof" ? "upload proof" : "declare"}{" "}
                during that window.
              </p>
              <div>
                <div className="relative mb-2">
                  <Search
                    size={16}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={selectionSearch}
                    onChange={(e) => setSelectionSearch(e.target.value)}
                    placeholder="Search by code or name..."
                    className={`w-full pl-8 pr-3 py-2 text-sm border rounded-xl ${
                      darkMode
                        ? "bg-gray-600 border-gray-500 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-xs ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {selectionDraftIds.length} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-xs text-redhalo-red hover:underline"
                      onClick={() =>
                        setSelectionDraftIds(
                          selectionEmployeeOptions.map((e) => Number(e.id))
                        )
                      }
                    >
                      Select filtered
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:underline"
                      onClick={() => setSelectionDraftIds([])}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div
                  className={`border rounded-xl max-h-64 overflow-y-auto ${
                    darkMode ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  {selectionEmployeeOptions.length === 0 ? (
                    <p
                      className={`p-3 text-sm ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      No employees found
                    </p>
                  ) : (
                    selectionEmployeeOptions.map((emp) => {
                      const id = Number(emp.id);
                      const code =
                        emp.empCode ||
                        emp.emp_code ||
                        emp.employeeCode ||
                        "";
                      const name =
                        emp.user?.name ||
                        emp.fullName ||
                        emp.name ||
                        [emp.firstName, emp.lastName]
                          .filter(Boolean)
                          .join(" ") ||
                        "Unnamed";
                      const checked = selectionDraftIds.includes(id);
                      return (
                        <label
                          key={id}
                          className={`flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 cursor-pointer ${
                            darkMode
                              ? "border-gray-700 hover:bg-gray-700"
                              : "border-gray-100 hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setSelectionDraftIds((prev) =>
                                e.target.checked
                                  ? [...new Set([...prev, id])]
                                  : prev.filter((x) => x !== id)
                              );
                            }}
                            className="rounded-md"
                          />
                          <span className="font-medium">{code}</span>
                          <span
                            className={
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }
                          >
                            {name}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={() => closeEmployeeSelectionModal(true)}
                className={`px-4 py-2 text-sm rounded-full ${
                  darkMode
                    ? "bg-gray-600 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyEmployeeSelectionModal}
                className="px-4 py-2 text-sm rounded-full rh-btn-primary"
              >
                Apply Selection
              </button>
            </div>
          </div>
        </div>
      )}

      <TaxYearLockModal
        darkMode={darkMode}
        isOpen={showLockModal}
        onClose={() => setShowLockModal(false)}
        years={fyOptions}
        activeTaxYear={taxYear}
        lockedTaxYears={lockedTaxYears}
        onChangeLocked={setLockedTaxYears}
        canEdit={canEdit}
      />
    </>
  );
});

export default InvestmentDeclarationSettings;
